// libraries.
import { useForm, useFieldArray } from "react-hook-form";

// store.
import { useRulesSlice } from "@store/apiSlices/incentiveRules/slices/rules";
import { useGetRulesToolsQuery } from "@store/apiSlices/incentiveRules/rulesTools.endpoints";
import { useUserProfile } from "@shared/GoogleLoginButton/user-slice/useUserProfile.hook";

// interfaces.
import {
  RawConditionOperator,
  RulesSchema,
} from "@store/apiSlices/incentiveRules/interfaces/rawRules.interfaces";

// ─── Form types ───────────────────────────────────────────────────────────────

export interface SubConditionValues {
  left: string;
  type: RawConditionOperator;
  right: string;
}

export interface ActionValues {
  actionName: string;
  tool: string;
  operation: string;
  output: string;
  params: Record<string, string>;
}

export interface FormValues {
  rule_id: string;
  name: string;
  description: string;
  priority: number;
  is_active: boolean;
  conditionMode: "simple" | "and" | "or";
  conditionLeft: string;
  conditionType: RawConditionOperator;
  conditionRight: string;
  subConditions: SubConditionValues[];
  actions: ActionValues[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const parseValue = (v: string): string | number => {
  const n = Number(v);
  return v.trim() !== "" && !isNaN(n) ? n : v;
};

const emptySubCondition: SubConditionValues = { left: "", type: "equals", right: "" };
const emptyAction: ActionValues = { actionName: "", tool: "", operation: "", output: "", params: {} };

// ─── Hook ─────────────────────────────────────────────────────────────────────

interface Props {
  show: boolean;
  country: string;
  onClose: () => void;
}

export const useCreateRuleForm = ({ show, country, onClose }: Props) => {
  const { email } = useUserProfile();
  const { createRule, isSaving } = useRulesSlice();
  const { data: tools = [], isLoading: isLoadingTools } = useGetRulesToolsQuery(undefined, { skip: !show });

  const methods = useForm<FormValues>({
    defaultValues: {
      rule_id: "",
      name: "",
      description: "",
      priority: 10,
      is_active: true,
      conditionMode: "simple",
      conditionLeft: "",
      conditionType: "equals",
      conditionRight: "",
      subConditions: [emptySubCondition],
      actions: [emptyAction],
    },
  });

  const subConditionArray = useFieldArray({
    control: methods.control,
    name: "subConditions",
  });

  const actionArray = useFieldArray({
    control: methods.control,
    name: "actions",
  });

  const onSubmit = methods.handleSubmit(async (data) => {
    const schema: RulesSchema = country === "MX" ? "inventory_mx" : "inventory_co";

    const condition =
      data.conditionMode === "simple"
        ? { left: data.conditionLeft, type: data.conditionType, right: parseValue(data.conditionRight) }
        : {
            type: data.conditionMode as "and" | "or",
            conditions: data.subConditions.map((c) => ({
              left: c.left,
              type: c.type,
              right: parseValue(c.right),
            })),
          };

    const actions = data.actions.map(({ actionName, tool, output, params }) => ({
      name: actionName,
      tool,
      output,
      params: Object.fromEntries(Object.entries(params).map(([k, v]) => [k, parseValue(v)])),
    }));

    await createRule({
      rule_id: data.rule_id,
      schema,
      is_active: data.is_active ? "active" : "inactive",
      changed_by: email,
      rule: {
        id: data.rule_id,
        name: data.name,
        description: data.description,
        priority: data.priority,
        condition,
        actions,
      },
    });

    methods.reset();
    onClose();
  });

  return {
    methods,
    onSubmit,
    isSaving,
    tools,
    isLoadingTools,
    subConditionArray,
    actionArray,
    emptySubCondition,
    emptyAction,
  };
};
